import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
  Box,
  Button,
  Input,
  FormControl,
  FormLabel,
  Heading,
  Text,
  VStack,
  Image,
  Center,
  InputGroup,
  InputRightElement,
  IconButton,
  Alert,
  AlertIcon,
  Flex,
  useColorModeValue,
  FormErrorMessage,
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon, ArrowBackIcon } from '@chakra-ui/icons';
import { api } from '../../utils/api';
import logo from '../../Images/logo_tovi.png';

const ResetPassword = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isValidToken, setIsValidToken] = useState(true);

  const navigate = useNavigate();
  const { token } = useParams();

  // Dynamic colors
  const bgMain = useColorModeValue('white', 'gray.800');
  const shadow = useColorModeValue('lg', 'dark-lg');
  const inputBg = useColorModeValue('white', 'gray.700');
  const labelColor = useColorModeValue('gray.700', 'gray.300');
  const headingColor = useColorModeValue('gray.700', 'gray.100');
  const footerColor = useColorModeValue('gray.500', 'gray.400');

  // Validate token on component mount
  useEffect(() => {
    const validateToken = async () => {
      try {
        const response = await api.get(`api/webuser/reset-password/${token}`);
        
        if (!response || !response.ok) {
          setIsValidToken(false);
          setError('Link de recuperação inválido ou expirado.');
        }
      } catch (error) {
        setIsValidToken(false);
        setError('Link de recuperação inválido ou expirado.');
      }
    };

    if (token) {
      validateToken();
    }
  }, [token]);

  const passwordsMatch = password === confirmPassword;
  const isValidPassword = password.length >= 6;

  const submitHandler = async (e) => {
    e.preventDefault();
    
    if (!passwordsMatch) {
      setError('As senhas não coincidem.');
      return;
    }

    if (!isValidPassword) {
      setError('A senha deve ter pelo menos 6 caracteres.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await api.post(`api/webuser/reset-password/${token}`, { 
        password 
      });
      
      if (!response) {
        setIsLoading(false);
        return;
      }

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setError(data.message || 'Erro ao redefinir senha.');
      }
    } catch (error) {
      setError('Erro de conexão. Por favor, tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Flex minH="100vh" align="center" justify="center" bg="gray.50">
      <Box bg={bgMain} rounded="xl" boxShadow={shadow} p={8} w="100%" maxW="400px" textAlign="center">
        <Center mb={6}>
          <Image src={logo} alt="Handyman Logo" maxH="120px" objectFit="contain" />
        </Center>

        <Text fontSize="xl" fontWeight="bold" color="teal.600" mb={2}>
          TOVI App Administração
        </Text>

        <Heading as="h1" size="lg" mb={8} color={headingColor}>
          Redefinir Senha
        </Heading>

        {error && (
          <Alert status="error" mb={6} borderRadius="md">
            <AlertIcon />
            {error}
          </Alert>
        )}

        {success ? (
          <VStack spacing={4}>
            <Alert status="success" borderRadius="md">
              <AlertIcon />
              <Box>
                <Text fontWeight="bold">Senha redefinida com sucesso!</Text>
                <Text fontSize="sm" mt={2}>
                  Você será redirecionado para a página de login...
                </Text>
              </Box>
            </Alert>
          </VStack>
        ) : !isValidToken ? (
          <VStack spacing={4}>
            <Text color="red.500" fontSize="sm">
              Este link de recuperação é inválido ou expirou.
            </Text>
            <Link to="/recuperar-senha" style={{ width: '100%' }}>
              <Button
                colorScheme="teal"
                width="full"
                size="lg"
              >
                Solicitar Novo Link
              </Button>
            </Link>
            <Link to="/login" style={{ width: '100%' }}>
              <Button
                leftIcon={<ArrowBackIcon />}
                variant="outline"
                width="full"
                size="md"
              >
                Voltar ao Login
              </Button>
            </Link>
          </VStack>
        ) : (
          <>
            <Text fontSize="sm" color={labelColor} mb={6}>
              Digite sua nova senha abaixo.
            </Text>

            <form onSubmit={submitHandler}>
              <VStack spacing={5}>
                <FormControl id="password" isRequired isInvalid={password && !isValidPassword}>
                  <FormLabel color={labelColor}>Nova Senha</FormLabel>
                  <InputGroup>
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Mínimo 6 caracteres"
                      size="md"
                      focusBorderColor="teal.500"
                      bg={inputBg}
                    />
                    <InputRightElement>
                      <IconButton
                        size="sm"
                        variant="ghost"
                        onClick={() => setShowPassword(!showPassword)}
                        aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                        icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                      />
                    </InputRightElement>
                  </InputGroup>
                  <FormErrorMessage>A senha deve ter pelo menos 6 caracteres.</FormErrorMessage>
                </FormControl>

                <FormControl id="confirmPassword" isRequired isInvalid={confirmPassword && !passwordsMatch}>
                  <FormLabel color={labelColor}>Confirmar Nova Senha</FormLabel>
                  <InputGroup>
                    <Input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Digite a senha novamente"
                      size="md"
                      focusBorderColor="teal.500"
                      bg={inputBg}
                    />
                    <InputRightElement>
                      <IconButton
                        size="sm"
                        variant="ghost"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        aria-label={showConfirmPassword ? 'Ocultar senha' : 'Mostrar senha'}
                        icon={showConfirmPassword ? <ViewOffIcon /> : <ViewIcon />}
                      />
                    </InputRightElement>
                  </InputGroup>
                  <FormErrorMessage>As senhas não coincidem.</FormErrorMessage>
                </FormControl>

                <Button
                  colorScheme="teal"
                  type="submit"
                  width="full"
                  size="lg"
                  mt={4}
                  isLoading={isLoading}
                  loadingText="Redefinindo..."
                  isDisabled={!password || !confirmPassword || !passwordsMatch || !isValidPassword}
                >
                  Redefinir Senha
                </Button>

                <Link to="/login" style={{ width: '100%' }}>
                  <Button
                    leftIcon={<ArrowBackIcon />}
                    variant="outline"
                    width="full"
                    size="md"
                  >
                    Voltar ao Login
                  </Button>
                </Link>
              </VStack>
            </form>
          </>
        )}

        <Text mt={8} fontSize="sm" color={footerColor}>
          © {new Date().getFullYear()} TOVI App. Todos os direitos reservados.
        </Text>
      </Box>
    </Flex>
  );
};

export default ResetPassword;
